import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';

import { useIdentifyUser } from '@/lib/posthog/hooks/useIdentifyUser';
import { PortalBillingProvider } from '@/modules/billing/components/PortalBillingProvider';
import { CompanyProfileCheckDialog } from '@/modules/companies/components/companyProfileCheck/CompanyProfileCheckDialog';
import { useBreadcrumbs } from '@/modules/hiring-manager/hooks/useBreadcrumbs';
import { useCurrentHMPageConfig } from '@/modules/hiring-manager/hooks/useCurrentHMPageConfig';
import { usePortalLayoutState } from '@/modules/hiring-manager/hooks/usePortalLayoutState';
import { DocumentMetadata } from '@/modules/layouts/components/DocumentMetadata';
import { PortalSidebar } from '@/modules/layouts/components/PortalSidebar';
import { PortalSidePanelSlot } from '@/modules/layouts/components/portalSidePanelSlot';
import { PortalNotifications } from '@/modules/notifications/components/portalNotifications/PortalNotifications';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/modules/ui/components/breadcrumb/Breadcrumb';
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/modules/ui/components/sidebar/Sidebar';
import { TooltipGroup } from '@/modules/ui/components/tooltip/Tooltip';

const PORTAL_THEME_CLASS = 'portal';

/** Collapsing the rail writes this cookie (Sidebar.tsx); read it so a reload keeps the choice. */
const isRailCollapsed = () =>
  document.cookie.split('; ').includes('sidebar_state=false');

/**
 * Portal chrome for `/hm/*`: an off-canvas side rail, a slim
 * top bar with the sidebar toggle, breadcrumb and notifications, the page
 * body, and a slot beside it for a page's `PortalSidePanel`.
 *
 * The redesigned look is the `.portal` token block in `index.css`.
 * It is applied to <body> rather than a wrapper so portalled surfaces (the
 * mobile sidebar sheet, menus, popovers) share the tokens and typeface.
 */
export function PortalLayout() {
  useIdentifyUser();

  const breadcrumbs = useBreadcrumbs();
  const currentPageConfig = useCurrentHMPageConfig();
  const pageTitle = currentPageConfig?.metadata?.title ?? 'Finlay';
  const scrollsItself = currentPageConfig?.layout === 'full-height';

  const { hasFinishedSignupFlow, tenantSelection, ...sidebarState } =
    usePortalLayoutState();
  const [sidePanelSlot, setSidePanelSlot] = useState<HTMLElement | null>(null);
  const { pathname } = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.body.classList.add(PORTAL_THEME_CLASS);
    return () => document.body.classList.remove(PORTAL_THEME_CLASS);
  }, []);

  // The page body scrolls in its own frame, which outlives route changes:
  // each page opens at the top, not where the last one was scrolled to.
  useLayoutEffect(() => {
    scrollRef.current?.scrollTo(0, 0);
  }, [pathname]);

  if (!hasFinishedSignupFlow) {
    return <Navigate to="/sign-up/get-started" replace />;
  }

  return (
    <PortalBillingProvider tenantId={tenantSelection.selectedTenantId}>
      <DocumentMetadata title={pageTitle} />
      <CompanyProfileCheckDialog />

      <SidebarProvider
        defaultOpen={!isRailCollapsed()}
        style={{ '--sidebar-width': '15rem' } as React.CSSProperties}
      >
        <TooltipGroup>
          <PortalSidebar tenantSelection={tenantSelection} {...sidebarState} />

          <SidebarInset className="h-svh min-w-0 overflow-hidden">
            <header className="bg-background flex h-12 shrink-0 items-center gap-3 border-b px-4 md:px-6">
              <SidebarTrigger
                className="hover:text-foreground size-7 rounded-md text-neutral-500"
                title="Toggle navigation"
              />
              <Breadcrumb>
                <BreadcrumbList className="gap-1.5 text-[13px] font-medium sm:gap-1.5">
                  {breadcrumbs.map((breadcrumb) => (
                    <React.Fragment key={breadcrumb.path}>
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to={breadcrumb.path}>{breadcrumb.title}</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator />
                    </React.Fragment>
                  ))}
                  <BreadcrumbItem>
                    <BreadcrumbPage className="font-medium">
                      {pageTitle}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
              <PortalNotifications />
            </header>

            <div className="flex min-h-0 flex-1">
              {scrollsItself ? (
                <div className="flex min-w-0 flex-1 flex-col">
                  <PortalSidePanelSlot value={sidePanelSlot}>
                    <Outlet />
                  </PortalSidePanelSlot>
                </div>
              ) : (
                // `overflow-y-auto` alone makes x scrollable too, so anything a
                // little too wide let a sideways swipe pan the whole page.
                // Wide content (tables) scrolls in its own frame instead.
                <div
                  className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto"
                  ref={scrollRef}
                >
                  <div className="mx-auto w-full max-w-[1040px] px-4 pt-10 pb-24 md:px-10">
                    <PortalSidePanelSlot value={sidePanelSlot}>
                      <Outlet />
                    </PortalSidePanelSlot>
                  </div>
                </div>
              )}
              {/* `contents`: a panel rendered here is a flex item of the row. */}
              <div className="contents" ref={setSidePanelSlot} />
            </div>
          </SidebarInset>
        </TooltipGroup>
      </SidebarProvider>
    </PortalBillingProvider>
  );
}
